import type { FrecencyData, FrecencyStorage } from '../actions/types'

const STORAGE_KEY = 'buffer-command-bar-frecency'
const MAX_ITEMS = 100
const MAX_RECENT_USES = 10
const HALF_LIFE_DAYS = 30
const MOMENTUM_WINDOW_MS = 6 * 60 * 60 * 1000 // 6 hours
const MOMENTUM_THRESHOLD = 2

/**
 * Calculate the frecency score for an action based on its usage data.
 *
 * The algorithm combines:
 * 1. Recency: Exponential decay with 30-day half-life
 * 2. Frequency: Log-compressed to prevent dominance
 * 3. Momentum: 50% boost if used twice in last 6 hours
 */
export function calculateFrecencyScore(data: FrecencyData): number {
  const now = Date.now()
  const daysSinceUse = (now - data.lastUsed) / (1000 * 60 * 60 * 24)

  // Time decay: half-life of 30 days
  const recencyScore = Math.pow(0.5, daysSinceUse / HALF_LIFE_DAYS)

  // Frequency: log-compressed to prevent dominance
  const frequencyScore = Math.log10(data.useCount + 1)

  // Momentum: 50% boost if used multiple times recently
  const recentCount = data.recentUses.filter(
    (t) => now - t < MOMENTUM_WINDOW_MS,
  ).length
  const momentumBoost = recentCount >= MOMENTUM_THRESHOLD ? 1.5 : 1

  return frequencyScore * recencyScore * momentumBoost
}

function isValidFrecencyData(item: unknown): item is FrecencyData {
  return (
    typeof item === 'object' &&
    item !== null &&
    'actionId' in item &&
    typeof item.actionId === 'string' &&
    'lastUsed' in item &&
    typeof item.lastUsed === 'number' &&
    'useCount' in item &&
    typeof item.useCount === 'number' &&
    'recentUses' in item &&
    Array.isArray(item.recentUses)
  )
}

/**
 * Load frecency data from localStorage
 */
export function loadFrecencyStorage(): FrecencyStorage {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed: unknown = JSON.parse(stored)
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        const validated: FrecencyStorage = {}
        let hasInvalid = false
        for (const [key, value] of Object.entries(parsed)) {
          if (isValidFrecencyData(value)) {
            validated[key] = value
          } else {
            hasInvalid = true
          }
        }
        if (hasInvalid) {
          if (Object.keys(validated).length === 0) {
            localStorage.removeItem(STORAGE_KEY)
          } else {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(validated))
          }
        }
        return validated
      }
      localStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // Ignore parse errors
  }
  return {}
}

/**
 * Save frecency data to localStorage
 */
export function saveFrecencyStorage(storage: FrecencyStorage): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storage))
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}

/**
 * Record a use of an action and update its frecency data
 */
export function recordActionUse(actionId: string): FrecencyStorage {
  const storage = loadFrecencyStorage()
  const now = Date.now()

  const existing = storage[actionId]
  const recentUses = existing?.recentUses ?? []

  // Add current timestamp and keep only the last N uses
  const updatedRecentUses = [...recentUses, now].slice(-MAX_RECENT_USES)

  storage[actionId] = {
    actionId,
    lastUsed: now,
    useCount: (existing?.useCount ?? 0) + 1,
    recentUses: updatedRecentUses,
  }

  // Prune old entries if we have too many
  const entries = Object.entries(storage)
  if (entries.length > MAX_ITEMS) {
    // Sort by frecency score and keep top items
    const sortedEntries = entries
      .map(([id, data]) => ({
        id,
        data,
        score: calculateFrecencyScore(data),
      }))
      .toSorted((a, b) => b.score - a.score)
      .slice(0, MAX_ITEMS)

    const pruned: FrecencyStorage = {}
    for (const entry of sortedEntries) {
      pruned[entry.id] = entry.data
    }
    saveFrecencyStorage(pruned)
    return pruned
  }

  saveFrecencyStorage(storage)
  return storage
}

/**
 * Get the frecency score for an action, or 0 if not found
 */
export function getFrecencyScore(actionId: string): number {
  const storage = loadFrecencyStorage()
  const data = storage[actionId]
  if (!data) {
    return 0
  }
  return calculateFrecencyScore(data)
}

/**
 * Sort actions by frecency score (descending)
 */
export function sortByFrecency<T extends { id: string }>(items: T[]): T[] {
  const storage = loadFrecencyStorage()

  return items.toSorted((a, b) => {
    const scoreA = storage[a.id] ? calculateFrecencyScore(storage[a.id]) : 0
    const scoreB = storage[b.id] ? calculateFrecencyScore(storage[b.id]) : 0
    return scoreB - scoreA
  })
}

/**
 * Clear all frecency data (for testing or reset)
 */
export function clearFrecencyStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore errors
  }
}
